import { RouteName } from '@/navigation/types';

const COLLEAGUE_PERFORMANCE_PROFILE_PATH_PREFIX = '/Colleagues/profile/performance/view/';

export const CreateProfileReturnMode = {
    CompetenciesTab: 'competenciesTab',
    CompetencyProfilesAdmin: 'competencyProfilesAdmin',
    PerformanceProfile: 'performanceProfile',
    ColleaguePerformanceProfile: 'colleaguePerformanceProfile',
} as const;

export type CreateProfileReturnModeType =
    (typeof CreateProfileReturnMode)[keyof typeof CreateProfileReturnMode];

export type CreateProfileWizardLocationState = {
    returnMode: CreateProfileReturnModeType;
    returnEmployeeId?: string;
    returnEmployeeName?: string;
    /**
     * Where to land once the profile exists, when that is neither the wizard's origin nor the
     * page implied by `returnMode` — the wizard may be reached from another module entirely.
     */
    returnPath?: string;
};

const QUERY_KEY_RETURN_MODE = 'returnMode';
const QUERY_KEY_RETURN_EMPLOYEE_ID = 'returnEmployeeId';
const QUERY_KEY_RETURN_EMPLOYEE_NAME = 'returnEmployeeName';
const QUERY_KEY_RETURN_PATH = 'returnPath';

type BuildCreateProfileWizardLocationStateParams =
    | { returnMode: typeof CreateProfileReturnMode.CompetenciesTab }
    | {
          returnMode:
              | typeof CreateProfileReturnMode.CompetencyProfilesAdmin
              | typeof CreateProfileReturnMode.PerformanceProfile
              | typeof CreateProfileReturnMode.ColleaguePerformanceProfile;
          returnEmployeeId: string;
          returnEmployeeName: string;
          returnPath?: string;
      };

export const buildCreateProfileWizardLocationState = (
    params: BuildCreateProfileWizardLocationStateParams,
): CreateProfileWizardLocationState => {
    if (params.returnMode === CreateProfileReturnMode.CompetenciesTab) {
        return { returnMode: params.returnMode };
    }

    return {
        returnMode: params.returnMode,
        returnEmployeeId: params.returnEmployeeId,
        returnEmployeeName: params.returnEmployeeName,
        returnPath: params.returnPath,
    };
};

export const buildCreateProfileWizardSearch = (state: CreateProfileWizardLocationState): string => {
    const searchParams = new URLSearchParams();
    searchParams.set(QUERY_KEY_RETURN_MODE, state.returnMode);

    if (state.returnEmployeeId) {
        searchParams.set(QUERY_KEY_RETURN_EMPLOYEE_ID, state.returnEmployeeId);
    }

    if (state.returnEmployeeName) {
        searchParams.set(QUERY_KEY_RETURN_EMPLOYEE_NAME, state.returnEmployeeName);
    }

    if (state.returnPath) {
        searchParams.set(QUERY_KEY_RETURN_PATH, state.returnPath);
    }

    return `?${searchParams.toString()}`;
};

export const resolveCreateProfileWizardLocationState = ({
    locationState,
    search,
}: {
    locationState?: CreateProfileWizardLocationState;
    search: string;
}): CreateProfileWizardLocationState | undefined => {
    if (locationState?.returnMode) {
        return locationState;
    }

    const searchParams = new URLSearchParams(search);
    const returnMode = searchParams.get(
        QUERY_KEY_RETURN_MODE,
    ) as CreateProfileReturnModeType | null;
    if (!returnMode) {
        return undefined;
    }

    return {
        returnMode,
        returnEmployeeId: searchParams.get(QUERY_KEY_RETURN_EMPLOYEE_ID) ?? undefined,
        returnEmployeeName: searchParams.get(QUERY_KEY_RETURN_EMPLOYEE_NAME) ?? undefined,
        returnPath: searchParams.get(QUERY_KEY_RETURN_PATH) ?? undefined,
    };
};

export const resolveCreateProfileWizardReturnPath = (
    state?: CreateProfileWizardLocationState,
): string => {
    switch (state?.returnMode) {
        case CreateProfileReturnMode.CompetencyProfilesAdmin:
            return RouteName.CompetencyProfilesAdmin;
        case CreateProfileReturnMode.PerformanceProfile:
            return state.returnEmployeeId
                ? RouteName.PerformanceProfile.replace(':id', state.returnEmployeeId)
                : `${RouteName.Competencies}?tab=profiles`;
        case CreateProfileReturnMode.ColleaguePerformanceProfile:
            return state.returnEmployeeId
                ? `${COLLEAGUE_PERFORMANCE_PROFILE_PATH_PREFIX}${state.returnEmployeeId}`
                : `${RouteName.Competencies}?tab=profiles`;
        case CreateProfileReturnMode.CompetenciesTab:
        default:
            return `${RouteName.Competencies}?tab=profiles`;
    }
};
